import React from 'react';

import type { AiAction } from '~/components/AiChat/types';
import Text from '~/components/core/TextComponents/Text';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import cn from '~/Utils/cn';

import { ACTION_STATE_CHIP, ACTION_WAITING_REASON_CHIP } from '../constants';
import type { ActionState, StateChipLook } from '../types';
import { actionStateLabel, actionWaitingReason } from '../utils';

/**
 * A state chip, whatever kind of thing's state it labels.
 *
 * Sized to its label, on the wrapping line over a card's title, and never wider than that line: a
 * label too long for it is cut short with an ellipsis, its full text a hover away, rather than
 * folding onto a second line or pushing the chip out of the card.
 */
export const StateChip: React.FC<StateChipLook> = ({ label, className }) => (
  <div className={cn('flex w-fit min-w-0 max-w-full items-center gap-8 rounded-full px-12 py-4', className)}>
    {/* The dot and the label both take the chip's text colour, so each state's chip class colours both. */}
    <span className="h-8 w-8 shrink-0 rounded-full bg-current" aria-hidden />
    {/* Lifted over an activity card's click overlay only while cut short, so its full text is a hover
    away, and a click on a label that fits still opens the log. */}
    <OverflowTextWithToolTip
      tooltipTitle={label}
      textWrapperClassName="data-[overflowing=true]:relative data-[overflowing=true]:z-10"
    >
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} className="inline text-current">
        {label}
      </Text>
    </OverflowTextWithToolTip>
  </div>
);

interface ActionStateChipProps {
  state: ActionState;
  /** The row, for the two transitions a person makes — the chip names them. */
  action?: AiAction;
}

/**
 * What became of an action, in the colours of the state it is in.
 *
 * One component for both panels: the log and the activity panel show the same card's state, and a
 * handler who reads "Rejected by Dana Whitfield" in one and "Rejected" in the other has found a
 * disagreement, not a difference of context.
 *
 * A waiting action wears the chip of the cause CMS held it for, where CMS named one, instead of the generic one.
 */
const ActionStateChip: React.FC<ActionStateChipProps> = ({ state, action }) => {
  const waitingReason = actionWaitingReason(state, action);
  const { label, className }: StateChipLook = waitingReason
    ? ACTION_WAITING_REASON_CHIP[waitingReason.key]
    : { label: actionStateLabel(state, action), className: ACTION_STATE_CHIP[state] };

  return <StateChip label={label} className={className} />;
};

export default ActionStateChip;
